import { forwardRef, useEffect, useMemo, useState } from 'react';

import { useLanguage } from '@core/hook';
import { formatCurrency, formatCurrencySymbol, formatNumber } from '@core/util';

import { InputNumberProps } from '../interface';
import { InputTextBase } from './InputText/InputTextBase';

export const InputNumberBase = forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      label,
      size,
      disabled: _disabled,
      field,
      value: _value,
      currency,
      isPositive,
      fractionDigits,
      ...inputProps
    }: InputNumberProps,
    refForward,
  ) => {
    const { language } = useLanguage();

    const numberFormatConfig = {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    };

    const {
      currency: currencySymbol,
      group: groupSeparator,
      decimal: decimalSeparator,
    } = useMemo(() => {
      const example = 1234.5;
      const parts = new Intl.NumberFormat(
        language,
        currency ? { style: 'currency', currency, ...numberFormatConfig } : numberFormatConfig,
      ).formatToParts(example);

      return parts.reduce(
        (acc, { type, value }) => {
          if (type in acc) {
            acc[type as keyof typeof acc] =
              type === 'currency' ? formatCurrencySymbol(value) : value;
          }
          return acc;
        },
        { currency: '$', group: ',', decimal: '.' },
      );
    }, [currency, language, fractionDigits]);

    const getInputValue = (
      fieldValue: number,
      useGrouping = true,
      shouldAddCurrency = false,
    ): string => {
      const config = {
        ...numberFormatConfig,
        useGrouping,
      };

      if (!fieldValue) return '';

      if (shouldAddCurrency && currency) {
        return formatCurrency(language, currency, fieldValue, config);
      } else {
        return formatNumber(language, fieldValue, config);
      }
    };

    const getFieldValue = (inputValue: string): number => {
      if (!inputValue) {
        return 0;
      }

      const normalized = inputValue
        .replace(currencySymbol, '')
        .replace(new RegExp(`\\${groupSeparator}`, 'g'), '')
        .replace(decimalSeparator, '.');

      const value = parseFloat(normalized);

      if (isNaN(value)) {
        return 0;
      }

      return isPositive ? Math.abs(value) : value;
    };

    const [inputValue, setInputValue] = useState<string>(getInputValue(field.value));

    useEffect(() => {
      if (field.value && (!inputValue || field.value !== getFieldValue(inputValue))) {
        setInputValue(getInputValue(field.value));
        field.onBlur();
      }

      if (field.value === 0) {
        // allow things like to be inputted: '0,0' '-0', '-0,0', ',0', '-.0', '-0,00.0'
        setInputValue(inputValue.match(/^[-]?0*([.,]?0*)?$/) ? inputValue : '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    return (
      <InputTextBase
        label={label}
        {...inputProps}
        ref={field?.ref || refForward}
        value={inputValue}
        onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(value);
          field.onChange(getFieldValue(value));
        }}
        onBlur={() => {
          const inputValue = getInputValue(field.value, true, true);
          const fieldValue = getFieldValue(inputValue);
          field.onChange(fieldValue);
          setInputValue(fieldValue === 0 ? '' : inputValue);
          field.onBlur();
        }}
        onFocus={() => {
          setInputValue(getInputValue(field.value, false));
        }}
      />
    );
  },
);

InputNumberBase.displayName = 'InputNumberBase';
